import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Autocomplete,
  Banner,
  Button,
  Input,
  Label,
  LayerCard,
  Loader,
  Select,
  useKumoToastManager,
} from '@cloudflare/kumo'
import type {
  AdminRoomView,
  AvailabilitySlot,
  Coworker,
  MyRoomView,
  RoomBookingDto,
} from '@hub-wifi/shared'
import { apiRequest } from '../../api/client'
import { useTranslation } from '../../i18n'
import { DayNavigator } from '../day-navigator/day-navigator'
import { addDaysInTz, todayYmdInTz } from '../../lib/calendar-time'
import { describeBookingError, nextDay, toIsoOffset } from './utils'

interface RoomOption {
  id: string
  name: string
}

interface FormData {
  roomId: string
  coworkerId: string
  date: string
  startTime: string
  endTime: string
}

const emptyForm: FormData = {
  roomId: '',
  coworkerId: '',
  date: '',
  startTime: '',
  endTime: '',
}

export type BookingFormScope = { kind: 'admin'; buildingId: string } | { kind: 'self' }

export interface BookingFormCoreProps {
  bookingId?: string
  scope: BookingFormScope
  onSuccess: (booking: RoomBookingDto) => void
  onCancel: () => void
}

interface Endpoints {
  listRooms: (token: string) => Promise<RoomOption[]>
  listCoworkers: ((token: string) => Promise<Coworker[]>) | null
  getBooking: (token: string, bookingId: string) => Promise<RoomBookingDto>
  getAvailability: (
    token: string,
    roomId: string,
    qs: string,
  ) => Promise<{ slots: AvailabilitySlot[] }>
  createBooking: (
    token: string,
    body: { roomId: string; coworkerId?: string; startAt: string; endAt: string },
  ) => Promise<RoomBookingDto>
  updateBooking: (
    token: string,
    bookingId: string,
    body: { startAt: string; endAt: string },
  ) => Promise<RoomBookingDto>
}

function buildEndpoints(scope: BookingFormScope): Endpoints {
  if (scope.kind === 'admin') {
    const buildingId = scope.buildingId
    return {
      listRooms: async (token) => {
        // Admin endpoint returns all rooms; only those mapped to a mailbox are bookable.
        const rs = await apiRequest<AdminRoomView[]>(`/api/buildings/${buildingId}/rooms`, {
          token,
        })
        return rs.filter((r) => r.mailbox).map((r) => ({ id: r.id, name: r.name }))
      },
      listCoworkers: async (token) => {
        const cws = await apiRequest<Coworker[]>(`/api/coworkers`, { token })
        return cws.filter((cw) => cw.status === 'active')
      },
      getBooking: (token, bookingId) =>
        apiRequest<RoomBookingDto>(`/api/buildings/${buildingId}/bookings/${bookingId}`, { token }),
      getAvailability: (token, roomId, qs) =>
        apiRequest<{ slots: AvailabilitySlot[] }>(
          `/api/buildings/${buildingId}/rooms/${roomId}/availability?${qs}`,
          { token },
        ),
      createBooking: (token, body) =>
        apiRequest<RoomBookingDto>(`/api/buildings/${buildingId}/bookings`, {
          method: 'POST',
          token,
          body,
        }),
      updateBooking: (token, bookingId, body) =>
        apiRequest<RoomBookingDto>(`/api/buildings/${buildingId}/bookings/${bookingId}`, {
          method: 'PATCH',
          token,
          body,
        }),
    }
  }
  return {
    listRooms: async (token) => {
      const resp = await apiRequest<{ rooms: MyRoomView[] }>('/api/my/rooms', { token })
      // Filter to bookable rooms only — mirrors the admin-scope behavior. The
      // edit path's pre-selected booking room is guaranteed bookable at
      // create-time, so it still resolves here.
      return resp.rooms.filter((r) => r.bookable).map((r) => ({ id: r.id, name: r.name }))
    },
    listCoworkers: null,
    getBooking: (token, bookingId) =>
      apiRequest<RoomBookingDto>(`/api/my/bookings/${bookingId}`, { token }),
    getAvailability: (token, roomId, qs) =>
      apiRequest<{ slots: AvailabilitySlot[] }>(`/api/my/rooms/${roomId}/availability?${qs}`, {
        token,
      }),
    createBooking: (token, body) =>
      apiRequest<RoomBookingDto>('/api/my/bookings', {
        method: 'POST',
        token,
        body: { roomId: body.roomId, startAt: body.startAt, endAt: body.endAt },
      }),
    updateBooking: (token, bookingId, body) =>
      apiRequest<RoomBookingDto>(`/api/my/bookings/${bookingId}`, {
        method: 'PATCH',
        token,
        body,
      }),
  }
}

function coworkerLabel(cw: Coworker): string {
  return cw.name ? `${cw.name} <${cw.email}>` : cw.email
}

export function BookingFormCore({ bookingId, scope, onSuccess, onCancel }: BookingFormCoreProps) {
  const isEdit = Boolean(bookingId)
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const todayYmd = todayYmdInTz(tz)

  const showCoworkerPicker = scope.kind === 'admin'
  const endpoints = useMemo(() => buildEndpoints(scope), [scope])

  const [form, setForm] = useState<FormData>(emptyForm)
  const [rooms, setRooms] = useState<RoomOption[]>([])
  const [coworkers, setCoworkers] = useState<Coworker[]>([])
  // Free-form text bound to <Autocomplete>. The coworker is "selected"
  // only when this text exactly matches a known coworker's label — that
  // resolution is what populates `form.coworkerId` (see handler below).
  const [coworkerInput, setCoworkerInput] = useState('')
  const [editedRange, setEditedRange] = useState<{ startAt: string; endAt: string } | null>(null)
  // On the edit path, the booking's room might have drifted to non-bookable
  // since the booking was created. `listRooms` filters those out (correct for
  // create), so the locked Select would render '' for the room. Stash the
  // room's name from the existing-booking response and fall back to it.
  const [editingRoomName, setEditingRoomName] = useState<string | null>(null)
  const [availability, setAvailability] = useState<AvailabilitySlot[] | null>(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityNote, setAvailabilityNote] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function init() {
      try {
        const token = await getAccessTokenSilently()
        const [roomList, coworkerList] = await Promise.all([
          endpoints.listRooms(token),
          endpoints.listCoworkers
            ? endpoints.listCoworkers(token)
            : Promise.resolve<Coworker[]>([]),
        ])
        if (cancelled) return
        setRooms(roomList)
        setCoworkers(coworkerList)
        if (!bookingId && roomList.length > 0) {
          setForm((prev) => ({ ...prev, roomId: roomList[0]!.id }))
        }
        if (bookingId) {
          const existing = await endpoints.getBooking(token, bookingId)
          if (cancelled) return
          const startD = new Date(existing.startAt)
          const endD = new Date(existing.endAt)
          // Format as local YYYY-MM-DD / HH:mm (NOT toISOString which is UTC).
          const datePart = `${startD.getFullYear()}-${String(startD.getMonth() + 1).padStart(2, '0')}-${String(startD.getDate()).padStart(2, '0')}`
          const startTime = `${String(startD.getHours()).padStart(2, '0')}:${String(startD.getMinutes()).padStart(2, '0')}`
          const endTime = `${String(endD.getHours()).padStart(2, '0')}:${String(endD.getMinutes()).padStart(2, '0')}`
          setForm({
            roomId: existing.roomId,
            coworkerId: existing.coworkerId,
            date: datePart,
            startTime,
            endTime,
          })
          const existingCoworker = coworkerList.find((cw) => cw.id === existing.coworkerId)
          if (existingCoworker) setCoworkerInput(coworkerLabel(existingCoworker))
          setEditedRange({ startAt: existing.startAt, endAt: existing.endAt })
          if (existing.roomName) setEditingRoomName(existing.roomName)
        }
      } catch (err) {
        if (!cancelled) setError(describeBookingError(err, t))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void init()
    return () => {
      cancelled = true
    }
  }, [bookingId, endpoints, getAccessTokenSilently, t])

  useEffect(() => {
    if (!form.roomId || !form.date) {
      setAvailability(null)
      setAvailabilityNote(null)
      return
    }
    let cancelled = false
    async function check() {
      setAvailabilityLoading(true)
      try {
        const token = await getAccessTokenSilently()
        // Probe the whole day in the local TZ: [00:00, next-day 00:00) so the
        // last half-hour slot (23:30 → 24:00) is included.
        const from = toIsoOffset(form.date, '00:00')
        const to = toIsoOffset(nextDay(form.date), '00:00')
        const qs = new URLSearchParams({ from, to, interval: '30' })
        const resp = await endpoints.getAvailability(token, form.roomId, qs.toString())
        if (cancelled) return
        setAvailability(resp.slots)
        setAvailabilityNote(null)
      } catch (err) {
        if (cancelled) return
        setAvailability(null)
        setAvailabilityNote(describeBookingError(err, t))
      } finally {
        if (!cancelled) setAvailabilityLoading(false)
      }
    }
    void check()
    return () => {
      cancelled = true
    }
  }, [form.roomId, form.date, endpoints, getAccessTokenSilently, t])

  // Whether the requested [startTime, endTime] window overlaps any busy slot.
  // In edit mode, ignore slots that fall fully inside the booking's own
  // existing time range — those are reported as busy because the server is
  // mirroring the booking we're editing. UX nicety; the server still
  // authoritatively rejects real conflicts on PATCH.
  const conflict = useMemo(() => {
    if (!availability || !form.startTime || !form.endTime || !form.date) return false
    const reqStart = new Date(toIsoOffset(form.date, form.startTime)).getTime()
    const reqEnd = new Date(toIsoOffset(form.date, form.endTime)).getTime()
    if (reqEnd <= reqStart) return false
    const editedStart = editedRange ? new Date(editedRange.startAt).getTime() : null
    const editedEnd = editedRange ? new Date(editedRange.endAt).getTime() : null
    return availability.some((s) => {
      if (s.free) return false
      const ss = new Date(s.startAt).getTime()
      const se = new Date(s.endAt).getTime()
      if (editedStart != null && editedEnd != null && ss >= editedStart && se <= editedEnd) {
        return false
      }
      return ss < reqEnd && se > reqStart
    })
  }, [availability, form.date, form.startTime, form.endTime, editedRange])

  function onCoworkerInputChange(next: string) {
    setCoworkerInput(next)
    const matched = coworkers.find((cw) => coworkerLabel(cw) === next)
    setForm((prev) => ({ ...prev, coworkerId: matched?.id ?? '' }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      const token = await getAccessTokenSilently()
      const startAt = toIsoOffset(form.date, form.startTime)
      const endAt = toIsoOffset(form.date, form.endTime)
      let dto: RoomBookingDto
      if (isEdit && bookingId) {
        dto = await endpoints.updateBooking(token, bookingId, { startAt, endAt })
        toasts.add({ variant: 'success', title: t('bookings.updateSuccess') })
      } else {
        dto = await endpoints.createBooking(token, {
          roomId: form.roomId,
          coworkerId: showCoworkerPicker ? form.coworkerId : undefined,
          startAt,
          endAt,
        })
        toasts.add({ variant: 'success', title: t('bookings.createSuccess') })
      }
      onSuccess(dto)
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describeBookingError(err, t),
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    )
  }

  return (
    <>
      {error && (
        <Banner variant="error" className="mb-3">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {rooms.length === 0 && !error && (
        <Banner variant="alert" className="mb-3">
          {t('bookings.noBookableRooms')}
        </Banner>
      )}

      <LayerCard>
        <LayerCard.Primary>
          <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label>{t('bookings.field.room')}</Label>
              <Select
                value={form.roomId}
                onValueChange={(v) => {
                  if (v) setForm({ ...form, roomId: v as string })
                }}
                disabled={isEdit}
                renderValue={(v) => rooms.find((r) => r.id === v)?.name ?? editingRoomName ?? ''}
              >
                {rooms.map((r) => (
                  <Select.Option key={r.id} value={r.id}>
                    {r.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {showCoworkerPicker && (
              <div className="flex flex-col gap-1">
                <Label>{t('bookings.field.coworker')}</Label>
                <Autocomplete
                  items={coworkers}
                  value={coworkerInput}
                  onValueChange={onCoworkerInputChange}
                  disabled={isEdit}
                  itemToStringValue={(item: unknown) => coworkerLabel(item as Coworker)}
                >
                  <Autocomplete.InputGroup placeholder={t('bookings.field.coworkerPlaceholder')} />
                  <Autocomplete.Content>
                    <Autocomplete.List>
                      {(item: unknown) => {
                        const cw = item as Coworker
                        return (
                          <Autocomplete.Item key={cw.id} value={cw}>
                            {coworkerLabel(cw)}
                          </Autocomplete.Item>
                        )
                      }}
                    </Autocomplete.List>
                    <Autocomplete.Empty>{t('bookings.field.coworkerEmpty')}</Autocomplete.Empty>
                  </Autocomplete.Content>
                </Autocomplete>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="flex flex-col gap-1">
                <Label>{t('bookings.field.date')}</Label>
                <DayNavigator
                  value={form.date}
                  timezone={tz}
                  min={isEdit ? undefined : todayYmd}
                  onChange={(date) => setForm((f) => ({ ...f, date }))}
                  onPrev={() =>
                    setForm((f) => ({ ...f, date: addDaysInTz(f.date || todayYmd, -1, tz) }))
                  }
                  onNext={() =>
                    setForm((f) => ({ ...f, date: addDaysInTz(f.date || todayYmd, 1, tz) }))
                  }
                  disabledPrev={!isEdit && (!form.date || form.date <= todayYmd)}
                  prevLabel={t('dayNavigator.prevDay')}
                  nextLabel={t('dayNavigator.nextDay')}
                  openLabel={t('dayNavigator.openCalendar')}
                  todayLabel={t('dayNavigator.today')}
                  placeholder={t('bookings.field.date')}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>{t('bookings.field.start')}</Label>
                <Input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  required
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>{t('bookings.field.end')}</Label>
                <Input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  required
                />
              </div>
            </div>

            {availabilityLoading && (
              <div className="text-sm text-kumo-subtle">{t('bookings.availability.checking')}</div>
            )}
            {!availabilityLoading && availabilityNote && (
              <Banner variant="alert">{availabilityNote}</Banner>
            )}
            {!availabilityLoading &&
              !availabilityNote &&
              form.startTime &&
              form.endTime &&
              availability !== null && (
                <Banner variant={conflict ? 'error' : 'default'}>
                  {conflict ? t('bookings.availability.conflict') : t('bookings.availability.free')}
                </Banner>
              )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" type="button" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={submitting}
                disabled={
                  submitting ||
                  !form.roomId ||
                  (showCoworkerPicker && !form.coworkerId) ||
                  !form.date ||
                  !form.startTime ||
                  !form.endTime ||
                  conflict
                }
              >
                {t('common.save')}
              </Button>
            </div>
          </form>
        </LayerCard.Primary>
      </LayerCard>
    </>
  )
}
